import { useEffect } from "react";

import { Icon } from "atoms/icon/Icon";
import ChevronRight from "atoms/icon/icons/chevron_right_small.svg";
import { BadgeTag } from "atoms/tag/BadgeTag";
import { DescriptiveTag } from "atoms/tag/DescriptiveTag";
import { JustForYouTag } from "atoms/tag/JustForYouTag";
import { useIntersectionObserver } from "hooks/useIntersectionObserver";
import { usePrefetchPDP } from "hooks/usePrefetchPDP";
import { useFeatureFlag } from "services/Flags";
import { formatCurrency } from "toolbox/Money";
import { formatBillingPeriodInterval } from "utils/billingPeriod.helpers";

interface ShopMembershipListItemProps {
  title: string;

  /** List of included items in this shop item */
  includedList?: Array<string> | null;

  /** Item Image */
  imageUrl?: string | null;

  /** Tag for item (ie: Most Popular, New, etc.) */
  tag?: string | null;

  concerns?: Array<string> | null;

  /** Standard item cost (in smallest monetary unit) */
  price: number;

  onClick?: () => void;

  /** Max discount for Product */
  discount?: string | null;

  /** Custom color for tag (site wide promo) */
  customColor?: string | null;

  /** Hide benefits and price from users */
  guestMode?: boolean;

  /** Message to display for clicking into the detail view */
  detailMessage?: string | null;

  billingPeriodInterval?: string;
  billingPeriodIntervalCount?: number;

  /** Whether this item is AI recommended */
  isRecommended?: boolean;

  /** Optional: ID of the membership to enable prefetching */
  membershipId?: string;
}

export const ShopMembershipListItem = ({
  title,
  includedList,
  imageUrl,
  tag,
  concerns,
  price,
  discount,
  customColor,
  guestMode,
  onClick,
  detailMessage,
  billingPeriodInterval,
  billingPeriodIntervalCount,
  isRecommended = false,
  membershipId,
}: ShopMembershipListItemProps) => {
  const [ref, isIntersecting] = useIntersectionObserver<HTMLDivElement>({
    rootMargin: "200px",
    threshold: 0,
    triggerOnce: true,
  });

  const { prefetchMembershipPDP } = usePrefetchPDP();

  useEffect(() => {
    if (!isIntersecting || !membershipId) return;
    prefetchMembershipPDP(membershipId);
  }, [isIntersecting, membershipId, prefetchMembershipPDP]);

  const isFlexibleMembershipsEnabled = useFeatureFlag(
    "flexible_membership_terms"
  );
  return (
    <div
      ref={ref}
      className="flex cursor-pointer flex-col overflow-hidden rounded-lg border border-neutral-300 bg-white shadow-md backdrop-blur-sm transition-shadow active:scale-[0.98]"
      onClick={onClick}
    >
      <div
        className="relative h-[190px] w-full bg-cover bg-top"
        style={{
          backgroundImage: `url(${imageUrl})`,
        }}
      >
        {discount && (
          <div className="absolute left-2 top-2 z-10">
            <DescriptiveTag
              color={customColor ? "custom" : "primary"}
              size="small"
              customColor={customColor}
            >
              {discount}
            </DescriptiveTag>
          </div>
        )}
        {tag && (
          <div className="flex-wrap px-5 py-5">
            <BadgeTag>{tag}</BadgeTag>
          </div>
        )}
        {/* Transparent gradient fade from image to card */}
        <div className="absolute bottom-0 h-8 w-full bg-gradient-to-b from-transparent to-white/80" />
      </div>
      <div id="listItemContentWrapper" className="pl-[22px] pr-3 pt-3">
        {isRecommended && (
          <div className="mb-2">
            <JustForYouTag />
          </div>
        )}
        <div className="text-left font-serif text-h2 leading-tight tracking-tight text-primary">
          {title}
        </div>
        {concerns && concerns.length > 0 && (
          <div className="flex flex-wrap justify-start gap-3 px-4 pb-3">
            {concerns?.slice(0, 3).map((concern) => (
              <div key={concern}>
                <DescriptiveTag color="secondary" size="medium">
                  {concern}
                </DescriptiveTag>
              </div>
            ))}
          </div>
        )}
        {includedList && includedList.length > 0 && (
          <div className="flex flex-col justify-center pb-[18px] pt-[14px] text-body2 text-primary">
            <ul className="flex list-outside list-disc flex-col gap-2 pl-5 opacity-80">
              {includedList?.slice(0, 3).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="flex w-full items-center justify-between bg-one px-[22px] py-2">
        {guestMode && (
          <div className="text-body2 text-primary">Sign up to see price</div>
        )}
        {!guestMode && (
          <>
            <div className="text-body2 font-bold text-primary">
              {isFlexibleMembershipsEnabled ? (
                <span>
                  {formatBillingPeriodInterval(
                    price,
                    billingPeriodInterval ?? null,
                    billingPeriodIntervalCount ?? null
                  )}
                </span>
              ) : (
                <>
                  <span>{formatCurrency(price)}</span>
                  <span>/month</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-1">
              <div className="text-body2 font-bold text-brand-color">
                {detailMessage || "See all benefits"}
              </div>
              <Icon svg={ChevronRight} size="medium" color="brandColor" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
